import axios from "axios";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "./constants";
import { jwtDecode } from "jwt-decode";

const usersPath = "users/";
const currentUserPath = "current-user/";
const createTaskPath = "create-task/";
const tasksPath = "task-list/";
const userTasksPath = "user-task-list/";
const categoriesPath = "category-list/";
const userCategoriesPath = "user-category-list/";
const categoryUsersPath = "category-user-list/";
const categoryTasksPath = "category-task-list/";
const createCommentPath = "create-comment/";
const taskCommentsPath = "task-comment-list/";
const commentsPath = "comment-list/";
const createTaskUserRelationPath = "create-task-user-relation/";
const modifyTaskUserRelationPath = "task-user-relation-list/";
const deleteTaskUserRelationsPath = "delete-task-user-relation-list/";
const userWorklogsPath = "user-worklog-list/";
const worklogsPath = "worklog-list/";
const modifyWorklogPath = "modify-worklog/";
const workTypesPath = "work-type-list/";
const uploadTaskFilePath = "upload-task-file/";
const downloadTaskFilePath = "download-task-file/";
const modifyTaskFilePath = "modify-task-file/";
const clientsPath = "create-view-clients/";
const modifyClientPath = "modify-client/";
const offersPath = "create-view-offers/";
const modifyOfferPath = "modify-offer/";
const filterPreferencesPath = "filter-preferences/";
const statusChoicesPath = "status-choices/";
const exportExcelPath = "export-excel/";
const exportActivitiesPath = "export-activities/";
const generateRegistrationTokenPath = "generate-registration-token/";
const categoriesFromTokenPath = "get-categories-from-token/";
const registerUserPath = "register/";
const loginPath = "login/";
const verify2faPath = "verify-2fa/";
const genQrCodePath = "generate-qr/";
const currentSettingsPath = "current-settings/";
const monthlyDistinctsPath = "monthly-distincts/";
const monthlyEchoesPath = "monthly-echoes/";

export const fullDownloadTaskFilePath =
    import.meta.env.VITE_API_URL + downloadTaskFilePath;

const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL,
});

api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(ACCESS_TOKEN);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

const toQueryString = (params) => {
    let queryString = Object.keys(params)
        .map((key) => {
            if (params[key]) {
                let valueString = params[key].toString();
                if (params[key] instanceof Array) {
                    valueString = params[key]
                        .map((value) => {
                            return `${value}`;
                        })
                        .join(",");
                }
                return `${key}=${valueString}`;
            }
        })
        .join("&");
    return queryString !== "" ? "?" + queryString : "";
};

const getPath = ({ path = "", key = "", queryParams = "" }) => {
    key = key !== "" ? key.toString() + "/" : "";
    return `${path}${key}${toQueryString(queryParams)}`;
};

const isAccessTokenInvalidOrExpired = () => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (!token) {
        return true;
    }
    const decoded = jwtDecode(token);
    const tokenExpiration = decoded.exp;
    const now = Date.now() / 1000;
    return tokenExpiration < now;
};

const refreshAccessToken = async () => {
    try {
        const res = await api.post("token/refresh/", {
            refresh: localStorage.getItem(REFRESH_TOKEN),
        });
        localStorage.setItem(ACCESS_TOKEN, res.data.access);
    } catch (error) {
        console.error("Error refreshing access token: ", error);
    }
};

const tryRefreshAccessToken = async () => {
    const token = localStorage.getItem(ACCESS_TOKEN);
    if (isAccessTokenInvalidOrExpired(token)) {
        await refreshAccessToken();
    }
};

export const fetchData = async ({
    path = "",
    key = "",
    queryParams = {},
    log = "fetch",
}) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.get(getPath({ path, key, queryParams }));
        console.log(log + ":", response.data);
        return response.data;
    } catch (error) {
        console.error("Error fetching data: ", error);
        return null;
    }
};

export const postData = async (path, data) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.post(path, data);
        console.log("Response Data:", response.data);
        return response.data;
    } catch (error) {
        return Promise.reject(error);
    }
};

export const postDataBlob = async (path, data) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.post(path, data, { responseType: "blob" });
        console.log("Response Data:", response.data);
        return response.data;
    } catch (error) {
        return Promise.reject(error);
    }
};

export const deleteData = async ({ path = "", key = "", queryParams = {} }) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.delete(getPath({ path, key, queryParams }));
        console.log("Response Data:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error deleting data: ", error);
        return null;
    }
};

export const updateData = async (path, id, data) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.put(`${path}${id}/`, data);
        // console.log("Response Data:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error updating data: ", error);
        return null;
    }
};

export const patchData = async ({
    path = "",
    key = "",
    queryParams = {},
    data = {},
}) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.patch(
            getPath({ path, key, queryParams }),
            data
        );
        // console.log("Response Data:", response.data);
        return response.data;
    } catch (error) {
        console.error("Error patching data: ", error);
        return null;
    }
};

export const uploadFile = async ({ path = "", formData = null }) => {
    try {
        await tryRefreshAccessToken();
        const response = await api.post(path, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        });
        return response.data;
    } catch (error) {
        console.error("Error uploading file: ", error);
        return null;
    }
};

export const fetchCurrentUser = () =>
    fetchData({ path: currentUserPath, log: "fetch current user" });

export const fetchUsers = () =>
    fetchData({ path: usersPath, log: "fetch users" });

export const fetchCategoryUsers = (categoryId) =>
    fetchData({
        path: categoryUsersPath,
        key: categoryId,
        log: "fetch category users",
    });

export const fetchTasks = (queryParams) => {
    return fetchData({
        path: tasksPath,
        log: "fetch tasks",
        queryParams: queryParams,
    });
};

export const fetchUserTasks = () =>
    fetchData({ path: userTasksPath, log: "fetch user tasks" });

export const fetchCategories = () =>
    fetchData({ path: categoriesPath, log: "fetch categories" });

export const fetchUserCategories = () =>
    fetchData({ path: userCategoriesPath, log: "fetch user categories" });

export const fetchTaskComments = (taskId) => {
    return fetchData({
        path: taskCommentsPath,
        key: taskId,
        log: "fetch task comments",
    });
};

export const fetchUserWorklogs = (params) => {
    return fetchData({
        path: userWorklogsPath,
        queryParams: params,
        log: "fetch worklogs",
    });
};

export const fetchWorkTypes = (date) => {
    return fetchData({
        path: workTypesPath,
        log: "fetch work types",
    });
};

export const fetchClients = () => {
    return fetchData({
        path: clientsPath,
        log: "fetch clients",
    });
};

export const fetchFilterPreferences = (userId) => {
    return fetchData({
        path: filterPreferencesPath,
        key: userId,
        log: "fetch filter preferences",
    });
};

export const fetchStatusChoices = () => {
    return fetchData({ path: statusChoicesPath, log: "fetch status choices" });
};

export const generateRegistrationToken = () =>
    fetchData({ path: generateRegistrationTokenPath, log: "generate token" });

export const fetchCategoriesFromToken = (token) =>
    fetchData({
        path: `${categoriesFromTokenPath}${token}`,
        log: "fetch categories fron token",
    });

export const fetchCurrentSettings = () =>
    fetchData({ path: currentSettingsPath });

export const fetchMonthlyDistincts = (queryParams) =>
    fetchData({ path: monthlyDistinctsPath, queryParams: queryParams });

export const fetchMonthlyEchoes = (queryParams) =>
    fetchData({ path: monthlyEchoesPath, queryParams: queryParams });

export const fetchHasDistincts = () => fetchData({ path: "has-distincts/" });

export const fetchHasEchoes = () => fetchData({ path: "has-echoes/" });

export const registerUser = (data, token) => {
    return postData(`${registerUserPath}${token}/`, data);
};

export const createUser = (username) => postData(usersPath, { username });

export const createTask = (taskData) => postData(createTaskPath, taskData);

export const createComment = (data) => postData(createCommentPath, data);

export const createTaskUserRelation = (data) =>
    postData(createTaskUserRelationPath, data);

export const createWorklog = (data) => postData(worklogsPath, data);

export const createClient = (data) => postData(clientsPath, data);

export const createOffer = (data) => postData(offersPath, data);

export const createMonthlyDistinctsBatch = (data) => {
    postData(monthlyDistinctsPath, data);
};

export const createMonthlyEchoesBatch = (data) =>
    postData(monthlyEchoesPath, data);

export const deleteTask = (id) =>
    deleteData({
        path: tasksPath,
        key: id,
    });

export const deleteComment = (id) =>
    deleteData({ path: commentsPath, key: id });

export const deleteTaskUserRelation = (taskId, userId) =>
    deleteData({
        path: modifyTaskUserRelationPath,
        queryParams: { task: taskId, user: userId },
    });

export const deleteTaskUserRelations = (taskId, userIds) =>
    deleteData({
        path: deleteTaskUserRelationsPath,
        queryParams: { task: taskId, users: userIds },
    });

export const deleteWorklog = (id) =>
    deleteData({ path: modifyWorklogPath, key: id });

export const deleteTaskFile = (id) =>
    deleteData({ path: modifyTaskFilePath, key: id });

export const deleteClient = (id) =>
    deleteData({ path: modifyClientPath, key: id });

export const deleteOffer = (id) =>
    deleteData({ path: modifyOfferPath, key: id });

export const updateTask = (id, data) => updateData(tasksPath, id, data);

export const patchTask = (id, data) =>
    patchData({ path: tasksPath, key: id, data: data });

export const patchTaskUserRelation = (taskId, userId, data) =>
    patchData({
        path: modifyTaskUserRelationPath,
        queryParams: { task: taskId, user: userId },
        data,
    });

export const patchComment = (id, data) =>
    patchData({ path: commentsPath, key: id, data: data });

export const patchWorklog = (id, data) =>
    patchData({ path: modifyWorklogPath, key: id, data: data });

export const patchClient = (id, data) =>
    patchData({ path: modifyClientPath, key: id, data: data });

export const patchOffer = (id, data) =>
    patchData({ path: modifyOfferPath, key: id, data: data });

export const patchFilterPreferences = (userId, data) =>
    patchData({ path: filterPreferencesPath, key: userId, data: data });

export const patchMonthlyDistincts = (id, data) =>
    patchData({ path: monthlyDistinctsPath, key: id, data: data });

export const patchMonthlyEchoes = (id, data) =>
    patchData({ path: monthlyEchoesPath, key: id, data: data });

export const uploadTaskFile = (taskId, file) => {
    const formData = new FormData();
    formData.append("task", taskId);
    formData.append("file", file);
    return uploadFile({ path: uploadTaskFilePath, formData });
};

export const exportExcel = async ({ filename = "report", data }) => {
    try {
        const response = await api.post(exportActivitiesPath, data, {
            responseType: "blob",
            headers: {
                "Content-Type": "application/json",
            },
        });
        const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename + ".xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error("Error exporting excel: ", error);
        return null;
    }
};

export const exportActivities = (data) => postData("export-activities/", data);

export const login = (username, password) =>
    postData(loginPath, { username, password });

export const verify2fa = (username, password, code) =>
    postData(verify2faPath, { username, password, code });

export const generateQr = (username, password) =>
    postDataBlob(genQrCodePath, { username, password });

export default api;
